export const CHECKOUT_URL =
  'https://basketballcoachsystem.impultienda.ar/checkout'

export const IMAGES = {
  /**
   * Real Basketball Coach System™ master bundle mockup (supplied asset).
   * Never replace this with a generated image.
   */
  bundle:
    'https://cdn.magicpatterns.com/uploads/svnREVZqNi2ya91ASksGXi/ChatGPT_Image_9_sept_2026,_20_28_03.png',
}

export const heroHighlights: string[] = [
  '50 Ready-to-Run Practices',
  '100 Basketball Drills & Progressions',
  'Offense & Scoring Systems',
  'Defense & Team Systems',
  'Player Development',
  'Game Day & Season Management',
]

export type Stage = {
  label: string
  description: string
}

export const stages: Stage[] = [
  {
    label: 'Open',
    description: 'Open the system and go to the volume you need today.',
  },
  {
    label: 'Choose',
    description: 'Choose the practice, drill or system your team needs.',
  },
  {
    label: 'Coach',
    description: 'Take it to the court with a clear plan already built.',
  },
]

export type Volume = {
  number: number
  title: string
  benefit: string
  /**
   * Real volume cover supplied by the brand. Leave `null` to keep a clean
   * placeholder — never substitute a generated cover image.
   */
  image: string | null
}

export const volumes: Volume[] = [
  {
    number: 1,
    title: 'Ready-To-Run Practices™',
    benefit:
      '50 complete basketball practices built around clear objectives, timing, progression and game application.',
    image: null,
  },
  {
    number: 2,
    title: 'Drills & Progressions™',
    benefit:
      '100 basketball drills covering essential skills, decision-making and progressive player development.',
    image: null,
  },
  {
    number: 3,
    title: 'Offense & Scoring Systems™',
    benefit:
      'A structured approach to spacing, movement, cutting, screening, transition offense, decision-making and scoring.',
    image: null,
  },
  {
    number: 4,
    title: 'Defense & Team Systems™',
    benefit:
      'Build individual defensive fundamentals into organized team defense, help, recovery, rotations and defensive structure.',
    image: null,
  },
  {
    number: 5,
    title: 'Player Development™',
    benefit:
      'Create clearer development priorities, progressions and individual improvement pathways throughout the season.',
    image: null,
  },
  {
    number: 6,
    title: 'Game Day & Season Management™',
    benefit:
      'Organize the season, player roles, rotations, game-day preparation, timeouts, halftime and the demands of competitive coaching.',
    image: null,
  },
]

export type Bonus = {
  number: number
  title: string
  description: string
  /** Real bonus cover supplied by the brand. `null` keeps a clean placeholder. */
  image: string | null
}

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'The Practice Efficiency System™',
    description:
      'Run sharper practices with better transitions, less wasted time and greater player involvement.',
    image: null,
  },
  {
    number: 2,
    title: '50 Basketball Coaching Fixes™',
    description:
      'Practical solutions to common offensive, defensive, player-development and practice problems.',
    image: null,
  },
  {
    number: 3,
    title: 'The Small-Gym Coaching Guide™',
    description:
      'Adapt productive basketball practices to limited court space, equipment, roster sizes and shortened sessions.',
    image: null,
  },
  {
    number: 4,
    title: 'Basketball Coach Communication Playbook™',
    description:
      'Practical guidance for player feedback, team communication, roles, parents and difficult coaching conversations.',
    image: null,
  },
  {
    number: 5,
    title: 'The Competitive Practice Guide™',
    description:
      'Add meaningful competition, pressure, constraints and game-like intensity to everyday practice.',
    image: null,
  },
]

export const audiences: string[] = [
  'Youth basketball coaches',
  'School basketball coaches',
  'Club / AAU coaches',
  'Competitive team coaches',
]

export const beforePoints: string[] = [
  'Practices planned separately',
  'Drills saved everywhere',
  'Offensive ideas disconnected from development',
  'Defensive concepts without clear progression',
  'Game-day preparation rebuilt repeatedly',
  'Too many disconnected coaching resources',
]

export const afterPoints: string[] = [
  'One organized coaching library',
  '50 practices ready when needed',
  '100 drills organized by purpose',
  'Offense and defense connected to development',
  'Clearer player-development structure',
  'A professional system to use throughout the season',
]

export type Testimonial = {
  quote: string
  name: string
  role: string
}

export const testimonials: Testimonial[] = [
  {
    quote:
      'Practice planning used to take me an hour every night. Now I open Volume 1, pick the session that fits where we are, and I am done in minutes.',
    name: 'Michael R.',
    role: 'Youth club coach • Austin, TX',
  },
  {
    quote:
      'Having the drills organized by purpose is the part I did not expect to value most. I stop guessing what to run and my practices flow better.',
    name: 'James T.',
    role: 'School team coach • Manchester, UK',
  },
  {
    quote:
      'I have coached for eleven years and still use it every week. It is not teaching me basketball — it keeps everything I need in one place.',
    name: 'Chris M.',
    role: 'AAU program coach • Toronto, Canada',
  },
]

export const offerIncludes: string[] = [
  '6 Professional Coaching Volumes',
  '5 Practical Coaching Bonuses',
  '50 Ready-to-Run Practices',
  '100 Basketball Drills & Progressions',
  'Instant Digital Access',
]

export const finalHighlights: string[] = [
  '6 Professional Volumes',
  '5 Practical Bonuses',
  '50 Ready-to-Run Practices',
  '100 Basketball Drills',
]

export type Faq = {
  question: string
  answer: string
}

export const faqs: Faq[] = [
  {
    question: 'Is Basketball Coach System™ a course?',
    answer:
      'No. It is a professional digital coaching resource library, built for reference and day-to-day practice organization.',
  },
  {
    question: 'Is this software or an app?',
    answer:
      'No. There is no software or subscription. The system consists of digital professional coaching resources.',
  },
  {
    question: 'Who is this for?',
    answer:
      'Basketball coaches working with youth, school, club and competitive teams who want greater structure, organization and ready-to-use coaching resources.',
  },
  {
    question: 'Does it include complete practices?',
    answer: 'Yes. Volume 1 contains 50 Ready-to-Run Practices.',
  },
  {
    question: 'Does it include basketball drills?',
    answer: 'Yes. Volume 2 contains 100 Basketball Drills & Progressions.',
  },
  {
    question: 'Can experienced coaches use it?',
    answer:
      'Yes. The system is designed as a practical coaching library rather than a beginner coaching course.',
  },
  {
    question: 'How do I receive it?',
    answer: 'Digital access is provided after purchase.',
  },
]
